(() => {
  'use strict';

  const select = (selector, scope = document) => scope?.querySelector(selector) || null;
  const selectAll = (selector, scope = document) => scope ? Array.from(scope.querySelectorAll(selector)) : [];
  const configNode = select('[data-theme-config]');
  const config = configNode ? JSON.parse(configNode.textContent) : {};
  const i18n = config.i18n || {};
  const thresholds = Array.isArray(config.thresholds) ? config.thresholds.map(Number) : [60, 95, 150];
  const rewardsEnabled = config.rewardsEnabled === true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let adaptiveMotionLimited = reducedMotion;
  let cartState = null;
  let previousMilestone = 0;
  let toastTimer;
  let overlayHistoryBusy = false;
  const productCache = new Map();
  const savedStorageKey = 'fg_saved_for_later_v1';

  const motionLimited = () => adaptiveMotionLimited;

  const initMotionIntelligence = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const constrainedConnection = Boolean(connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType));
    const merchantMinimum = document.body.classList.contains('motion-minimal');
    adaptiveMotionLimited = adaptiveMotionLimited || constrainedConnection || merchantMinimum;
    document.documentElement.classList.toggle('motion-lite', adaptiveMotionLimited);
  };

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const formatMoney = cents => {
  const formatWithDelimiters = (
    value,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';

    const parts = (number / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousands
    );

    return precision
      ? `${parts[0]}${decimal}${parts[1]}`
      : parts[0];
  };

  const formats = {
    amount: formatWithDelimiters(cents, 2, ',', '.'),
    amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
    amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
    amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
    amount_no_decimals_with_space_separator: formatWithDelimiters(cents, 0, ' ', ','),
    amount_with_space_separator: formatWithDelimiters(cents, 2, ' ', ','),
    amount_with_period_and_space_separator: formatWithDelimiters(cents, 2, ' ', '.')
  };

  return String(config.moneyFormat || '€{{amount}}').replace(
    /\{\{\s*([a-z_]+)\s*\}\}/g,
    (match, token) =>
      Object.prototype.hasOwnProperty.call(formats, token)
        ? formats[token]
        : match
  );
};


  const showToast = message => {
    const toast = select('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const lockPage = locked => document.body.classList.toggle('is-locked', locked);

  const overlaySelector = '[data-mobile-menu].is-open, [data-search-overlay].is-open, [data-cart-drawer].is-open, [data-filters].is-open';

  const armOverlayHistory = () => {
    if (window.history.state?.fgOverlay) return;
    try { window.history.pushState({ ...(window.history.state || {}), fgOverlay: true }, '', window.location.href); }
    catch { /* Browser history is an enhancement. */ }
  };

  const releaseOverlayHistory = () => {
    if (!window.history.state?.fgOverlay || overlayHistoryBusy) return;
    overlayHistoryBusy = true;
    window.history.back();
    window.setTimeout(() => { overlayHistoryBusy = false; }, 500);
  };

  const setPanel = (panel, open, options = {}) => {
    if (!panel) return;
    const wasOpen = panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    if (options.backdrop) options.backdrop.classList.toggle('is-open', open);
    if (options.lock !== false) lockPage(open);
    if (open && !wasOpen && options.history !== false) armOverlayHistory();
    if (!open && wasOpen && options.history !== false && !select(overlaySelector)) releaseOverlayHistory();
    if (open && options.focus) window.setTimeout(() => options.focus.focus(), 450);
  };

  const closeAllPanels = (fromHistory = false) => {
    document.body.classList.remove('fg-cart-open');
    setPanel(select('[data-cart-drawer]'), false, { backdrop: select('.fg-cart-backdrop'), history: !fromHistory });
    setPanel(select('[data-mobile-menu]'), false, { backdrop: select('.fg-menu-backdrop'), history: !fromHistory });
    setPanel(select('[data-search-overlay]'), false, { history: !fromHistory });
    setPanel(select('[data-filters]'), false, { backdrop: select('.fg-filter-backdrop'), history: !fromHistory });
    select('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
  };

  const initVisualViewport = () => {
    const viewport = window.visualViewport;
    const update = () => document.documentElement.style.setProperty('--fg-visual-height', `${Math.round(viewport?.height || window.innerHeight)}px`);
    update();
    viewport?.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
  };

  const initIntro = () => {
    const intro = select('[data-intro]');
    if (!intro || motionLimited()) return;
    const complete = () => {
      if (intro.classList.contains('is-exiting')) return;
      intro.classList.add('is-exiting');
      window.setTimeout(() => intro.classList.add('is-complete'), 950);
    };
    select('[data-intro-skip]', intro)?.addEventListener('click', complete);
    window.setTimeout(complete, 2850);
  };

  const initReveal = () => {
    const items = selectAll('[data-reveal]');
    if (motionLimited() || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach(item => observer.observe(item));
  };

  const initHeader = () => {
    const header = select('[data-header]');
    let previousY = window.scrollY;
    let ticking = false;
    const update = () => {
      const currentY = window.scrollY;
      header?.classList.toggle('is-hidden', currentY > previousY && currentY > 180 && !document.body.classList.contains('is-locked'));
      previousY = currentY;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) window.requestAnimationFrame(update);
      ticking = true;
    }, { passive: true });

    const announcement = select('[data-announcement]');
    select('[data-announcement-close]')?.addEventListener('click', () => announcement?.classList.add('is-closed'));
    const deliveryToast = select('[data-delivery-toast]');
    if (deliveryToast && announcement) {
      const seconds = Number.parseFloat(getComputedStyle(announcement).getPropertyValue('--notice-delay')) || 3;
      const mobileNotice = window.matchMedia('(max-width: 649px)').matches;
      let showTimer;
      let hideTimer;
      let deliveryNoticeDismissed = false;
      let deliveryNoticeSeen = false;
      try { deliveryNoticeDismissed = window.localStorage.getItem('fg_delivery_notice_dismissed_v1') === 'true'; } catch { /* Storage is enhancement-only. */ }
      try { deliveryNoticeSeen = window.sessionStorage.getItem('fg_delivery_notice_seen_v1') === 'true'; } catch { /* Storage is enhancement-only. */ }

      const hideDeliveryNotice = ({ persist = false, rememberSession = mobileNotice } = {}) => {
        window.clearTimeout(showTimer);
        window.clearTimeout(hideTimer);
        deliveryToast.classList.remove('is-visible');
        deliveryToast.setAttribute('aria-hidden', 'true');
        try {
          if (rememberSession) window.sessionStorage.setItem('fg_delivery_notice_seen_v1', 'true');
          if (persist) window.localStorage.setItem('fg_delivery_notice_dismissed_v1', 'true');
        } catch { /* Storage is enhancement-only. */ }
      };

      if (!deliveryNoticeDismissed && (!mobileNotice || !deliveryNoticeSeen)) {
        showTimer = window.setTimeout(() => {
          deliveryToast.classList.add('is-visible');
          deliveryToast.setAttribute('aria-hidden', 'false');
          if (mobileNotice) hideTimer = window.setTimeout(() => hideDeliveryNotice(), 4500);
        }, seconds * 1000);
      }
      select('[data-delivery-close]')?.addEventListener('click', () => {
        hideDeliveryNotice({ persist: true, rememberSession: true });
      });
    }
  };

  const initNavigation = () => {
    const menu = select('[data-mobile-menu]');
    const backdrop = select('.fg-menu-backdrop');
    const menuTrigger = select('[data-menu-open]');
    const closeMenu = () => {
      setPanel(menu, false, { backdrop });
      document.body.classList.remove('fg-menu-open');
      menuTrigger?.setAttribute('aria-expanded', 'false');
    };
    menuTrigger?.addEventListener('click', () => {
      setPanel(menu, true, { backdrop });
      document.body.classList.add('fg-menu-open');
      menuTrigger.setAttribute('aria-expanded', 'true');
    });
    selectAll('[data-menu-close]').forEach(button => button.addEventListener('click', closeMenu));
    selectAll('[data-submenu-toggle]').forEach(button => button.addEventListener('click', () => {
      const submenu = button.closest('.fg-mobile-nav-item')?.querySelector('.fg-mobile-submenu');
      const open = submenu?.hasAttribute('hidden');
      if (open) submenu.removeAttribute('hidden'); else submenu?.setAttribute('hidden', '');
      button.setAttribute('aria-expanded', String(open));
      button.innerHTML = open ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"></path></svg>' : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>';
    }));
  };

  const initSearch = () => {
    const overlay = select('[data-search-overlay]');
    const input = select('[data-predictive-input]');
    const results = select('[data-predictive-results]');
    const discovery = select('[data-search-discovery]', overlay);
    let searchController;
    let debounce;
    const close = () => setPanel(overlay, false);
    selectAll('[data-search-open]').forEach(button => button.addEventListener('click', () => setPanel(overlay, true, { focus: input })));
    selectAll('[data-search-close]').forEach(button => button.addEventListener('click', close));
    input?.addEventListener('input', () => {
      window.clearTimeout(debounce);
      const term = input.value.trim();
      if (discovery) discovery.hidden = term.length >= 2;
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      debounce = window.setTimeout(async () => {
        searchController?.abort();
        searchController = new AbortController();
        try {
          const base = config.rootUrl || '/';
          const url = `${base}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=6`;
          const response = await fetch(url, { signal: searchController.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const data = await response.json();
          const products = data.resources?.results?.products || [];
          const collections = data.resources?.results?.collections || [];
          const productHTML = products.map(product => `<a href="${escapeHTML(product.url)}"><img src="${escapeHTML(product.image || '')}" alt=""><span><strong>${escapeHTML(product.title)}</strong><small>${escapeHTML(product.price || '')}</small></span></a>`).join('');
          const collectionHTML = collections.map(collection => `<a href="${escapeHTML(collection.url)}"><span><strong>${escapeHTML(collection.title)}</strong><small>Collection</small></span></a>`).join('');
          results.innerHTML = productHTML + collectionHTML || '<p>No systems found. Try another direction.</p>';
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '<p>Search is temporarily unavailable.</p>';
        }
      }, 220);
    });
  };

  const milestoneFor = total => {
    if (!rewardsEnabled) return 0;
    return thresholds.filter(value => total >= value).pop() || 0;
  };

  const celebrate = milestone => {
    if (!rewardsEnabled) return;
    if (motionLimited()) return;
    const celebration = select('[data-celebration]');
    if (!celebration) return;
    const label = milestone === thresholds[0]
      ? 'Free delivery unlocked'
      : milestone === thresholds[1]
        ? 'Free delivery + 10% unlocked'
        : 'Free delivery + 20% unlocked';
    celebration.innerHTML = `<div class="fg-milestone-card"><span>GRAVITY REWARD</span><strong>${escapeHTML(label)}</strong><small>Applied at checkout when the matching Shopify rule is active.</small></div>`;
    const colors = ['#ff4d00', '#ffb000', '#ffffff'];
    for (let index = 0; index < 34; index += 1) {
      const particle = document.createElement('i');
      particle.className = 'fg-particle';
      particle.style.setProperty('--particle-color', colors[index % colors.length]);
      particle.style.setProperty('--particle-x', `${(Math.random() - 0.5) * window.innerWidth * 0.9}px`);
      particle.style.setProperty('--particle-y', `${(Math.random() - 0.5) * window.innerHeight * 0.85}px`);
      celebration.appendChild(particle);
    }
    window.setTimeout(() => { celebration.innerHTML = ''; }, 2600);
  };

  const rewardCopy = total => {
    if (!rewardsEnabled) return '';
    const next = thresholds.find(value => total < value);
    if (!next) return i18n.allRewardsUnlocked || 'All gravity rewards are unlocked.';
    const difference = next - total;
    const label = next === thresholds[0] ? (i18n.freeDelivery || 'free delivery') : next === thresholds[1] ? (i18n.discount10 || '10% discount') : (i18n.discount20 || '20% discount');
    const template = i18n.addMoreToUnlock || 'Add __AMOUNT__ more to unlock __REWARD__.';
    return template.replace('__AMOUNT__', formatMoney(difference * 100)).replace('__REWARD__', label);
  };

  const readSavedItems = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(savedStorageKey) || '[]');
      return Array.isArray(saved) ? saved.slice(0, 20) : [];
    } catch { return []; }
  };

  const writeSavedItems = items => {
    try { window.localStorage.setItem(savedStorageKey, JSON.stringify(items.slice(0, 20))); }
    catch { showToast(i18n.savedItemsUnavailable || 'Saved items are unavailable in this browser.'); }
  };

  const fetchProduct = async handle => {
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${config.rootUrl || '/'}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then(response => {
        if (!response.ok) throw new Error('Product unavailable');
        return response.json();
      });
    productCache.set(handle, request);
    return request;
  };

  const cartLineHTML = item => {
    const variant = item.product_has_only_default_variant ? '' : `<span>${escapeHTML(item.variant_title || '')}</span>`;
    const properties = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_'))
      .map(([key, value]) => `<span>${escapeHTML(key)}: ${escapeHTML(value)}</span>`).join('');
    return `<article class="fg-cart-line" data-cart-line="${escapeHTML(item.key)}" data-product-handle="${escapeHTML(item.handle || '')}" data-variant-id="${item.variant_id}">
      <a class="fg-cart-line__image" href="${escapeHTML(item.url)}"><img src="${escapeHTML(item.image || '')}" width="180" height="210" alt=""></a>
      <div class="fg-cart-line__details"><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variant}${properties}
        <div class="fg-quantity"><button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity - 1}" aria-label="${escapeHTML(i18n.decreaseQuantity || 'Decrease quantity')}">−</button><span>${item.quantity}</span><button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHTML(i18n.increaseQuantity || 'Increase quantity')}">+</button></div>
        ${config.enableVariantSwap && !item.product_has_only_default_variant ? `<div class="fg-cart-variant-slot" data-cart-variant-slot><span>${escapeHTML(i18n.loadingSelections || 'Loading available selections…')}</span></div>` : ''}
        ${config.enableSaveLater ? `<button class="fg-save-for-later" type="button" data-save-for-later data-key="${escapeHTML(item.key)}">${escapeHTML(i18n.saveForLater || 'Save for later')}</button>` : ''}
      </div>
      <div class="fg-cart-line__amount"><strong>${formatMoney(item.final_line_price)}</strong><button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity="0">${escapeHTML(i18n.remove || 'Remove')}</button></div>
    </article>`;
  };

  const hydrateVariantSwitchers = async cart => {
    if (!config.enableVariantSwap) return;
    await Promise.all(cart.items.map(async item => {
      if (item.product_has_only_default_variant || !item.handle) return;
      const line = select(`[data-cart-line="${CSS.escape(item.key)}"]`);
      const slot = select('[data-cart-variant-slot]', line);
      if (!slot) return;
      try {
        const product = await fetchProduct(item.handle);
        const options = product.variants.map(variant => `<option value="${variant.id}"${Number(variant.id) === Number(item.variant_id) ? ' selected' : ''}${variant.available ? '' : ' disabled'}>${escapeHTML(variant.title)}${variant.available ? '' : ` - ${escapeHTML(i18n.soldOut || 'Sold out')}`}</option>`).join('');
        slot.innerHTML = `<label class="fg-cart-variant"><span>${escapeHTML(i18n.changeSelection || 'Change selection')}</span><select data-cart-variant data-key="${escapeHTML(item.key)}" aria-label="${escapeHTML(i18n.changeSelection || 'Change selection')}: ${escapeHTML(item.product_title)}">${options}</select></label><small>${escapeHTML(i18n.selectionAvailability || 'Availability and price refresh instantly.')}</small>`;
      } catch { slot.innerHTML = `<small>${escapeHTML(i18n.openProductToChange || 'Open the product page to change this selection.')}</small>`; }
    }));
  };

  const renderSavedItems = () => {
    const panel = select('[data-saved-panel]');
    const container = select('[data-saved-items]', panel);
    if (!panel || !container) return;
    const saved = readSavedItems();
    panel.hidden = saved.length === 0;
    container.innerHTML = saved.map(item => `<article class="fg-saved-item" data-saved-id="${item.variantId}"><img src="${escapeHTML(item.image || '')}" width="64" height="76" alt=""><span><strong>${escapeHTML(item.productTitle)}</strong><small>${escapeHTML(item.variantTitle || '')}</small><b>${formatMoney(item.price)}</b></span><div><button type="button" data-saved-add="${item.variantId}">${escapeHTML(i18n.moveToCart || 'Move to cart')}</button><button type="button" data-saved-remove="${item.variantId}">${escapeHTML(i18n.remove || 'Remove')}</button></div></article>`).join('');
  };

  const renderCartRecommendations = async cart => {
    const panel = select('[data-cart-recommendations]');
    const container = select('[data-cart-recommendation-items]', panel);
    if (!panel || !container || !config.enableCartRecommendations || !cart.items.length || !config.recommendationsUrl) {
      if (panel) panel.hidden = true;
      return;
    }
    try {
      const productId = cart.items[0].product_id;
      const response = await fetch(`${config.recommendationsUrl}.json?product_id=${productId}&limit=6&intent=related`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const data = await response.json();
      const cartProductIds = new Set(cart.items.map(item => Number(item.product_id)));
      const products = (data.products || []).filter(product => !cartProductIds.has(Number(product.id)) && product.available).slice(0, 2);
      panel.hidden = products.length === 0;
      container.innerHTML = products.map(product => {
        const variant = product.variants.find(item => item.available) || product.variants[0];
        const image = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url || '';
        return `<article><a href="${escapeHTML(product.url)}"><img src="${escapeHTML(image)}" width="72" height="84" alt=""><span><strong>${escapeHTML(product.title)}</strong><small>${formatMoney(variant.price)}</small></span></a><button type="button" data-recommendation-add="${variant.id}">Add +</button></article>`;
      }).join('');
    } catch { panel.hidden = true; }
  };

  const updateSavingsLedger = cart => {
    const original = Number(cart.original_total_price ?? cart.total_price);
    const discount = Number(cart.total_discount ?? Math.max(0, original - cart.total_price));
    const originalNode = select('[data-cart-original-total]');
    const discountNode = select('[data-cart-discount]');
    const deliveryNode = select('[data-cart-delivery-benefit]');
    if (originalNode) originalNode.textContent = formatMoney(original);
    if (discountNode) discountNode.textContent = discount > 0 ? `−${formatMoney(discount)}` : '-';
    if (deliveryNode) deliveryNode.textContent = rewardsEnabled ? (original >= thresholds[0] * 100 ? (i18n.eligible || 'Eligible') : `${formatMoney(thresholds[0] * 100 - original)} remaining`) : 'Calculated at checkout';

    const total = original / 100;
    const tierPercent = total >= thresholds[2] ? 20 : total >= thresholds[1] ? 10 : 0;
    const referralEligible = Boolean(cart.attributes?.fg_referral_token) && total >= thresholds[0];
    const selection = select('[data-reward-selection]');
    if (!selection) return;
    if (discount > 0) {
      const names = (cart.cart_level_discount_applications || []).map(application => application.title).filter(Boolean).join(', ');
      selection.textContent = `Active Shopify saving: ${formatMoney(discount)}${names ? ` · ${names}` : ''}. Checkout confirms final compatibility.`;
    } else if (tierPercent > 0) {
      selection.textContent = `Strongest Finiks tier at this subtotal: ${tierPercent}% off (estimated ${formatMoney(cart.total_price * tierPercent / 100)}). Referral offers cannot stack; checkout confirms eligibility.`;
    } else if (referralEligible) {
      selection.textContent = `Your referral invitation may unlock a 10% discount (estimated ${formatMoney(cart.total_price * 0.1)}). Checkout confirms eligibility.`;
    } else {
      selection.textContent = 'No percentage saving is active yet. Checkout validates every configured reward before payment.';
    }
  };

  const renderCart = (cart, allowCelebration = true) => {
    cartState = cart;
    const progressSubtotal = Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price);
    const total = progressSubtotal / 100;
    const percentage = Math.min(100, (total / thresholds[2]) * 100);
    selectAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    selectAll('[data-cart-open]').forEach(node => node.setAttribute('aria-label', `${i18n.openCart || 'Cart'}: ${cart.item_count}`));
    select('[data-cart-items]')?.replaceChildren();
    const items = select('[data-cart-items]');
    if (items) {
      if (cart.items.length) items.innerHTML = cart.items.map(cartLineHTML).join('');
      else items.innerHTML = `<div class="fg-cart-empty"><span>0.00</span><h3>${escapeHTML(i18n.emptyTitle || 'Your bag is ready for lift-off.')}</h3><p>${escapeHTML(i18n.emptyCopy || 'Find your next reason to move.')}</p><button class="fg-button fg-button--dark" type="button" data-cart-close>${escapeHTML(i18n.continueExploring || 'Continue exploring')}</button></div>`;
    }
    const cartFooter = select('[data-cart-footer]');
    if (cartFooter) cartFooter.hidden = cart.item_count === 0;
    const totalNode = select('[data-cart-total]');
    if (totalNode) totalNode.textContent = formatMoney(cart.total_price);
    const rewardFill = select('[data-reward-fill]');
    if (rewardFill) rewardFill.style.width = `${percentage}%`;
    const rewardText = select('[data-reward-copy]');
    if (rewardText) rewardText.textContent = rewardCopy(total);
    selectAll('[data-reward-marker]').forEach(marker => marker.classList.toggle('is-unlocked', total >= Number(marker.dataset.rewardMarker)));
    const global = select('[data-global-reward]');
    if (global) {
      global.hidden = cart.item_count === 0;
      select('[data-global-reward-fill]', global).style.width = `${percentage}%`;
      select('[data-global-reward-copy]', global).textContent = `${formatMoney(progressSubtotal)} ${i18n.inOrbit || 'in orbit'} · ${rewardCopy(total)}`;
    }
    updateSavingsLedger(cart);
    renderSavedItems();
    hydrateVariantSwitchers(cart);
    renderCartRecommendations(cart);
    const milestone = milestoneFor(total);
    if (allowCelebration && milestone > previousMilestone) celebrate(milestone);
    previousMilestone = milestone;
  };

  const fetchCart = async allowCelebration => {
    const response = await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(i18n.cartUnavailable || 'Cart unavailable');
    const cart = await response.json();
    renderCart(cart, allowCelebration);
    return cart;
  };

  const openCart = async () => {
    const drawer = select('[data-cart-drawer]');
    const backdrop = select('.fg-cart-backdrop');
    document.body.classList.add('fg-cart-open');
    setPanel(drawer, true, { backdrop });
    try { await fetchCart(false); } catch { showToast(i18n.cartRefreshFailed || 'Your cart could not be refreshed.'); }
  };

  const closeCart = () => {
    document.body.classList.remove('fg-cart-open');
    setPanel(select('[data-cart-drawer]'), false, { backdrop: select('.fg-cart-backdrop') });
  };

  const changeCartLine = async (key, quantity) => {
    try {
      const response = await fetch(`${config.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error(i18n.updateFailed || 'Update failed');
      renderCart(await response.json(), true);
      return true;
    } catch { showToast(i18n.cartUpdateFailed || 'We could not update that item. Please try again.'); return false; }
  };

  const addVariantToCart = async (variantId, quantity = 1, properties = undefined, sellingPlan = undefined) => {
    const item = { id: Number(variantId), quantity: Number(quantity) };
    if (properties && Object.keys(properties).length) item.properties = properties;
    if (sellingPlan) item.selling_plan = sellingPlan;
    const response = await fetch(`${config.cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || i18n.selectionUnavailable || 'This selection is unavailable.');
    }
    return response.json();
  };

  const changeCartVariant = async (key, variantId, selectNode) => {
    const item = cartState?.items.find(line => line.key === key);
    if (!item || Number(item.variant_id) === Number(variantId)) return;
    selectNode.disabled = true;
    let originalRemoved = false;
    try {
      const response = await fetch(`${config.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
      if (!response.ok) throw new Error('The previous selection could not be removed.');
      originalRemoved = true;
      await addVariantToCart(variantId, item.quantity, item.properties, item.selling_plan_allocation?.selling_plan?.id);
      await fetchCart(false);
      showToast(i18n.selectionUpdated || 'Selection updated');
      return true;
    } catch (error) {
      if (originalRemoved) {
        await addVariantToCart(item.variant_id, item.quantity, item.properties, item.selling_plan_allocation?.selling_plan?.id).catch(() => {});
      }
      showToast(error.message || 'We could not change that selection.');
      await fetchCart(false).catch(() => {});
      return false;
    } finally { selectNode.disabled = false; }
  };

  const saveCartItemForLater = async key => {
    const item = cartState?.items.find(line => line.key === key);
    if (!item) return;
    const saved = readSavedItems().filter(entry => Number(entry.variantId) !== Number(item.variant_id));
    saved.unshift({
      variantId: item.variant_id,
      productId: item.product_id,
      productTitle: item.product_title,
      variantTitle: item.variant_title,
      handle: item.handle,
      url: item.url,
      image: item.image,
      price: item.final_price,
      properties: item.properties || {},
      savedAt: new Date().toISOString()
    });
    writeSavedItems(saved);
    const moved = await changeCartLine(key, 0);
    if (!moved) return false;
    renderSavedItems();
    showToast(i18n.savedOnDevice || 'Saved on this device');
    return true;
  };

  const moveSavedToCart = async variantId => {
    const saved = readSavedItems();
    const item = saved.find(entry => Number(entry.variantId) === Number(variantId));
    if (!item) return;
    try {
      await addVariantToCart(item.variantId, 1, item.properties);
      writeSavedItems(saved.filter(entry => Number(entry.variantId) !== Number(variantId)));
      await fetchCart(true);
      renderSavedItems();
      showToast(i18n.movedToCart || 'Moved back to your flight bag');
    } catch (error) { showToast(error.message || i18n.savedSelectionUnavailable || 'That saved selection is unavailable.'); }
  };

  const removeSavedItem = variantId => {
    writeSavedItems(readSavedItems().filter(entry => Number(entry.variantId) !== Number(variantId)));
    renderSavedItems();
    showToast(i18n.removedSavedItem || 'Removed from saved items');
  };

  const addToCart = async form => {
    const button = select('button[type="submit"]', form);
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    try {
      const response = await fetch(`${config.cartAddUrl}.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'This selection is unavailable.');
      }
      await fetchCart(true);
      await openCart();
      window.FiniksIntent?.record('checkout_intent');
      showToast('Added to your flight bag');
    } catch (error) { showToast(error.message); }
    finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  const initCart = () => {
    document.addEventListener('click', event => {
      if (event.target.closest('[data-cart-open]')) { event.preventDefault(); window.FiniksIntent?.record('cart_opened'); openCart(); }
      if (event.target.closest('[data-cart-close]')) { event.preventDefault(); closeCart(); }
      const quantity = event.target.closest('[data-cart-quantity]');
      if (quantity) changeCartLine(quantity.dataset.key, Number(quantity.dataset.quantity)).then(updated => { if (updated && quantity.closest('.fg-cart-page')) window.location.reload(); });
      const saveButton = event.target.closest('[data-save-for-later]');
      if (saveButton) saveCartItemForLater(saveButton.dataset.key).then(updated => { if (updated && saveButton.closest('.fg-cart-page')) window.location.reload(); });
      const savedAdd = event.target.closest('[data-saved-add]');
      if (savedAdd) moveSavedToCart(savedAdd.dataset.savedAdd);
      const savedRemove = event.target.closest('[data-saved-remove]');
      if (savedRemove) removeSavedItem(savedRemove.dataset.savedRemove);
      const recommendation = event.target.closest('[data-recommendation-add]');
      if (recommendation) addVariantToCart(recommendation.dataset.recommendationAdd).then(() => fetchCart(true)).then(() => showToast('Added to your flight bag')).catch(error => showToast(error.message));
    });
    document.addEventListener('change', event => {
      const variant = event.target.closest('[data-cart-variant]');
      if (variant) changeCartVariant(variant.dataset.key, variant.value, variant).then(updated => { if (updated && variant.closest('.fg-cart-page')) window.location.reload(); });
    });
    document.addEventListener('submit', event => {
      const form = event.target.closest('[data-quick-add], [data-product-form]');
      if (!form) return;
      event.preventDefault();
      addToCart(form);
    });
    let noteTimer;
    document.addEventListener('input', event => {
      if (!event.target.matches('[data-cart-note]')) return;
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(() => fetch(`${config.cartUpdateUrl}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: event.target.value })
      }), 500);
    });
    renderSavedItems();
    fetchCart(false).catch(() => {});
  };

  const referralTokenPattern = /^FG-(?:[A-Z0-9]{4,10}|[A-Z0-9]{12})$/i;

  const syncReferralToken = async token => {
    if (!referralTokenPattern.test(token || '')) return;
    await fetch(`${config.cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { fg_referral_token: token.toUpperCase() } })
    });
  };

  const initReferralTracking = () => {
    const url = new URL(window.location.href);
    const incoming = String(url.searchParams.get('ref') || '').trim().toUpperCase();
    let token = '';
    if (referralTokenPattern.test(incoming)) {
      token = incoming;
      try { window.localStorage.setItem('fg_referral_token', token); } catch { /* Storage is enhancement-only. */ }
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } else {
      try { token = String(window.localStorage.getItem('fg_referral_token') || '').toUpperCase(); } catch { token = ''; }
    }
    if (referralTokenPattern.test(token)) syncReferralToken(token).catch(() => {});
  };

  const initReferralDashboard = () => {
    const hydrate = (panel, data) => {
      select('[data-referral-token]', panel).textContent = data.token || '';
      select('[data-referral-url]', panel).value = data.inviteUrl || '';
      select('[data-referral-count]', panel).textContent = Number(data.summary?.referrals || 0);
      select('[data-referral-pending]', panel).textContent = Number(data.summary?.pending || 0);
      select('[data-referral-rewarded]', panel).textContent = Number(data.summary?.rewarded || 0);
    };

    selectAll('[data-referral-launch]').forEach(trigger => trigger.addEventListener('click', async event => {
      event.preventDefault();
      const section = trigger.closest('[data-referral-section]');
      const panel = select('[data-referral-panel]', section);
      const status = select('[data-referral-status]', panel);
      const content = select('[data-referral-content]', panel);
      if (!panel) return;
      panel.hidden = false;
      content.hidden = true;
      status.hidden = false;
      status.textContent = i18n.referralLoadingInvitation || 'Preparing your private invitation…';
      panel.scrollIntoView({ behavior: motionLimited() ? 'auto' : 'smooth', block: 'nearest' });
      try {
        const response = await fetch(trigger.href, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (response.status === 401) {
          window.location.assign(config.accountUrl || '/account');
          return;
        }
        if (!response.ok) throw new Error('Referral space unavailable');
        const data = await response.json();
        hydrate(panel, data);
        status.hidden = true;
        content.hidden = false;
      } catch {
        status.textContent = i18n.referralSpaceUnavailable || 'Your referral space is temporarily unavailable. Please try again in a moment.';
      }
    }));

    selectAll('[data-referral-custom-form]').forEach(form => form.addEventListener('submit', async event => {
      event.preventDefault();
      const panel = form.closest('[data-referral-panel]');
      const input = select('input[name="code"]', form);
      const button = select('button[type="submit"]', form);
      const status = select('[data-referral-custom-status]', panel);
      const code = input?.value.trim().toUpperCase() || '';
      if (!/^[A-Z0-9]{4,10}$/.test(code)) {
        status.textContent = i18n.referralCodeFormat || 'Use 4 to 10 letters or numbers. Do not enter personal information.';
        status.className = 'is-error';
        input?.focus();
        return;
      }
      button.disabled = true;
      status.textContent = i18n.referralCheckingCode || 'Checking availability and securing your code…';
      status.className = '';
      try {
        const response = await fetch(`${config.referralUrl || '/apps/finiks-referrals'}/custom-code`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ code })
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) {
          window.location.assign(config.accountUrl || '/account');
          return;
        }
        if (!response.ok) throw new Error(data.error || i18n.referralCodeUnavailable || 'That code could not be activated.');
        hydrate(panel, data);
        status.textContent = i18n.referralCodeActive || 'Your custom code is active. Copy the refreshed invitation link above.';
        status.className = 'is-success';
        input.value = '';
      } catch (error) {
        status.textContent = error.message || i18n.referralCodeUnavailable || 'That code could not be activated.';
        status.className = 'is-error';
      } finally {
        button.disabled = false;
      }
    }));

    document.addEventListener('click', async event => {
      const copyButton = event.target.closest('[data-referral-copy]');
      if (!copyButton) return;
      const input = select('[data-referral-url]', copyButton.closest('[data-referral-panel]'));
      if (!input?.value) return;
      try {
        await navigator.clipboard.writeText(input.value);
      } catch {
        input.select();
        document.execCommand('copy');
      }
      copyButton.textContent = i18n.referralCopied || 'Copied';
      window.setTimeout(() => { copyButton.textContent = i18n.referralCopyLink || 'Copy link'; }, 1800);
    });
  };

  const initHero = () => {
    const hero = select('[data-orbit-hero]');
    if (!hero) return;
    const shoe = select('[data-orbit-shoe]', hero);
    if (!shoe || motionLimited() || !window.matchMedia('(pointer: fine)').matches) return;
    let frame;
    let bounds = hero.getBoundingClientRect();
    let latestX = 0;
    let latestY = 0;
    const refreshBounds = () => { bounds = hero.getBoundingClientRect(); };
    hero.addEventListener('pointerenter', refreshBounds, { passive: true });
    window.addEventListener('resize', refreshBounds, { passive: true });
    hero.addEventListener('pointermove', event => {
      latestX = event.clientX;
      latestY = event.clientY;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const x = (latestX - bounds.left) / bounds.width - 0.5;
        const y = (latestY - bounds.top) / bounds.height - 0.5;
        shoe.style.translate = `${x * 18}px ${y * 14}px`;
        frame = null;
      });
    }, { passive: true });
    hero.addEventListener('pointerleave', () => { shoe.style.translate = '0 0'; }, { passive: true });
    hero.addEventListener('pointerleave', () => { shoe.style.margin = ''; });
  };

  const initCloser = () => {
    selectAll('[data-closer]').forEach(component => {
      const slides = selectAll('[data-closer-slide]', component);
      let index = 0;
      const show = next => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
        const count = select('[data-closer-count]', component);
        if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      };
      select('[data-closer-prev]', component)?.addEventListener('click', () => show(index - 1));
      select('[data-closer-next]', component)?.addEventListener('click', () => show(index + 1));
    });
  };

  const initRails = () => {
    selectAll('[data-product-rail]').forEach(component => {
      const rail = select('[data-rail]', component);
      let offset = 0;
      const move = direction => {
        const card = select('.fg-product-card', rail);
        if (!card) return;
        const step = card.getBoundingClientRect().width + 20;
        const max = Math.max(0, rail.scrollWidth - window.innerWidth + window.innerWidth * 0.06);
        offset = Math.max(0, Math.min(max, offset + direction * step));
        rail.style.transform = `translateX(-${offset}px)`;
      };
      select('[data-rail-prev]', component)?.addEventListener('click', () => move(-1));
      select('[data-rail-next]', component)?.addEventListener('click', () => move(1));
    });
  };

  const initCrossUniverse = () => {
    selectAll('[data-cross-universe]').forEach(component => {
      if (component.dataset.loopReady) return;
      component.dataset.loopReady = 'true';
      const items = selectAll('[data-cross-item]', component);
      const render = () => {
        items.forEach(item => item.classList.remove('is-selected'));
        [...items].sort(() => Math.random() - 0.5).slice(0, 4).forEach((item, index) => {
          item.style.animationDelay = `${index * 80}ms`;
          item.classList.add('is-selected');
        });
      };
      select('[data-cross-refresh]', component)?.addEventListener('click', render);
      render();
      if (!motionLimited() && items.length > 4) {
        let timer = window.setInterval(render, 6800);
        component.addEventListener('mouseenter', () => window.clearInterval(timer));
        component.addEventListener('mouseleave', () => { timer = window.setInterval(render, 6800); });
      }
    });
  };

  const initOrbitLoop = () => {
    selectAll('[data-orbit-loop]').forEach(component => {
      if (component.dataset.loopReady) return;
      component.dataset.loopReady = 'true';
      const links = selectAll('[data-orbit-loop-link]', component);
      const progress = select('[data-orbit-loop-progress]', component);
      if (!links.length) return;
      let index = 0;
      let timer;
      const show = nextIndex => {
        index = nextIndex % links.length;
        links.forEach((link, linkIndex) => link.classList.toggle('is-active', linkIndex === index));
        if (progress) {
          progress.style.animation = 'none';
          void progress.offsetWidth;
          progress.style.animation = '';
        }
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (!motionLimited() && links.length > 1) timer = window.setInterval(() => show(index + 1), 5200);
      };
      show(0);
      start();
      component.addEventListener('mouseenter', stop);
      component.addEventListener('mouseleave', start);
      component.addEventListener('focusin', stop);
      component.addEventListener('focusout', start);
    });
  };

  const initViewCount = async () => {
    const counter = select('[data-site-view-count]');
    if (!counter || !config.viewCountUrl) return;
    let increment = true;
    try {
      increment = !window.sessionStorage.getItem('fg-view-counted');
      if (increment) window.sessionStorage.setItem('fg-view-counted', 'true');
    } catch { /* Private browsing can disable storage. */ }
    try {
      const url = new URL(config.viewCountUrl, window.location.origin);
      url.searchParams.set('count', increment ? '1' : '0');
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const payload = await response.json();
      const count = Number(payload.count ?? payload.views ?? payload.viewCount);
      if (Number.isFinite(count)) counter.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en').format(count);
    } catch { /* The counter is a progressive enhancement. */ }
  };

  const initProduct = () => {
    selectAll('[data-product-root]').forEach(root => {
      const dataNode = select('[data-product-variants]', root);
      if (!dataNode) return;
      const variants = JSON.parse(dataNode.textContent);
      const optionInputs = selectAll('[data-option-position]', root);
      const variantInput = select('[data-variant-id]', root);
      const addButton = select('[data-add-button]', root);
      const addLabel = select('[data-add-label]', root);
      const marketUnavailable = addButton?.dataset.marketUnavailable === 'true';
      const marketUnavailableLabel = addButton?.dataset.marketUnavailableLabel || 'Available in the United States only';
      const priceNode = select('[data-price]', root);

      const renderVariantPrice = variant => {
        if (!priceNode) return;
        const price = Number(variant.price || 0);
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const onSale = compareAtPrice > price;
        const measurement = variant.unit_price_measurement;
        const unitPrice = Number(variant.unit_price || 0);
        const unitMarkup = measurement && unitPrice
          ? `<small>${formatMoney(unitPrice)} / ${Number(measurement.reference_value) !== 1 ? escapeHTML(measurement.reference_value) : ''}${escapeHTML(measurement.reference_unit || '')}</small>`
          : '';
        priceNode.classList.toggle('is-sale', onSale);
        priceNode.innerHTML = `${onSale ? `<s>${formatMoney(compareAtPrice)}</s>` : ''}<span>${formatMoney(price)}</span>${unitMarkup}`;
      };

      const update = () => {
        const selected = [];
        const maxPosition = Math.max(0, ...optionInputs.map(input => Number(input.dataset.optionPosition)));
        for (let position = 1; position <= maxPosition; position += 1) selected.push(select(`[data-option-position="${position}"]:checked`, root)?.value);
        const variant = variants.find(item => item.options.every((option, index) => option === selected[index]));
        if (!variant) {
          if (variantInput) variantInput.value = '';
          if (addButton) addButton.disabled = true;
          if (addLabel) addLabel.textContent = config.i18n?.unavailable || 'Unavailable';
          return;
        }
        if (variantInput && String(variantInput.value) !== String(variant.id)) {
          variantInput.value = variant.id;
          variantInput.setAttribute('value', String(variant.id));
        }
        if (addButton) addButton.disabled = marketUnavailable || !variant.available;
        if (addLabel) addLabel.textContent = marketUnavailable ? marketUnavailableLabel : (variant.available ? (config.i18n?.addToCart || 'Add to cart') : (config.i18n?.soldOut || 'Sold out'));
        renderVariantPrice(variant);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.featured_media) {
          const media = select(`[data-media-id="${variant.featured_media.id}"]`, root);
          media?.scrollIntoView({ behavior: motionLimited() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
        }
      };
      optionInputs.forEach(input => input.addEventListener('change', () => { window.FiniksIntent?.record('variant_selection'); update(); }));
    });
  };

  const initFilters = () => {
    const filters = select('[data-filters]');
    const backdrop = select('.fg-filter-backdrop');
    select('[data-filter-toggle]')?.addEventListener('click', () => setPanel(filters, true, { backdrop }));
    selectAll('[data-filter-close]').forEach(button => button.addEventListener('click', () => setPanel(filters, false, { backdrop })));
    select('[data-sort-select]')?.addEventListener('change', event => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.assign(url);
    });
  };

  const initRecommendations = () => {
    selectAll('product-recommendations[data-url]').forEach(async component => {
      try {
        const response = await fetch(component.dataset.url);
        if (!response.ok) return;
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const incoming = select('product-recommendations', html);
        if (incoming?.innerHTML.trim()) component.innerHTML = incoming.innerHTML;
      } catch { /* Recommendations are enhancement-only. */ }
    });
  };

  const initCursor = () => {
    const cursor = select('[data-cursor]');
    if (!cursor || motionLimited() || !window.matchMedia('(pointer: fine)').matches) return;
    window.addEventListener('pointermove', event => {
      cursor.style.transform = `translate3d(${event.clientX - 15}px, ${event.clientY - 15}px, 0)`;
      cursor.classList.add('is-ready');
    }, { passive: true });
    document.addEventListener('pointerover', event => cursor.classList.toggle('is-active', Boolean(event.target.closest('a, button, input, select, textarea'))));
  };

  const initLocalization = () => {
    selectAll('[data-localization-select]').forEach(control => control.addEventListener('change', () => control.form?.submit()));
  };

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    closeAllPanels();
  });

  window.addEventListener('popstate', () => {
    overlayHistoryBusy = false;
    closeAllPanels(true);
  });

  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initCloser();
    initRails();
    initCrossUniverse();
    initOrbitLoop();
  });

  initMotionIntelligence();
  initVisualViewport();
  initIntro();
  initReveal();
  initHeader();
  initNavigation();
  initSearch();
  initReferralTracking();
  initCart();
  initReferralDashboard();
  initHero();
  initCloser();
  initRails();
  initCrossUniverse();
  initOrbitLoop();
  initViewCount();
  initProduct();
  initFilters();
  initRecommendations();
  initLocalization();
  initCursor();
                                                                                                                                                                                                          })();

(() => {
  const panel = document.querySelector('[data-finiks-intelligence]');
  const trigger = document.querySelector('[data-finiks-open]');
  const input = document.querySelector('#FiniksIntelligenceInput');
  const messages = document.querySelector('[data-finiks-messages]');
  const configNode = document.querySelector('[data-theme-config]');
  if (!panel || !trigger || !input || !messages || !configNode) return;
  const config = JSON.parse(configNode.textContent);
  const escapeHTML = value => String(value || '').replace(/[&<>'"]/g, character => ({ '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;' }[character]));
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const safePath = value => /^\/(?!\/)(?:cart|account|collections\/(?:all|[a-z0-9-]+)|policies\/(?:shipping-policy|refund-policy))$/.test(String(value || '')) ? value : '';
  const add = (message, user = false) => { const node = document.createElement('div'); node.className = `fg-intelligence__message${user ? ' fg-intelligence__message--user' : ''}`; node.textContent = message; messages.appendChild(node); messages.scrollTo({ top: messages.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' }); return node; };
  const prefetchPath = path => {
    const safe = safePath(path);
    if (!safe || document.head.querySelector(`link[data-finiks-prefetch="${safe}"]`)) return;
    const link = document.createElement('link'); link.rel = 'prefetch'; link.href = safe; link.as = 'document'; link.dataset.finiksPrefetch = safe; document.head.appendChild(link);
  };
  const addNextSteps = (container, nextSteps) => {
    if (!Array.isArray(nextSteps)) return;
    const safeSteps = nextSteps.filter(step => step && typeof step.label === 'string' && typeof step.href === 'string' && /^\/(?!\/)/.test(step.href)).slice(0, 3);
    if (!safeSteps.length) return;
    const links = document.createElement('div');
    links.className = 'fg-intelligence__results';
    safeSteps.forEach(step => { const link = document.createElement('a'); link.href = step.href; link.textContent = step.label.slice(0, 80); link.addEventListener('pointerenter', () => prefetchPath(step.href), { once: true }); link.addEventListener('focus', () => prefetchPath(step.href), { once: true }); link.addEventListener('click', () => recordLearningSignal('suggestion_clicked', step.href)); recordLearningSignal('suggestion_presented', step.href); links.appendChild(link); });
    container.appendChild(links);
  };
  let lastFocus = null;
  const open = () => { lastFocus = document.activeElement; panel.classList.add('is-open'); panel.setAttribute('aria-hidden','false'); trigger.setAttribute('aria-expanded','true'); input.focus(); };
  const close = () => { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden','true'); trigger.setAttribute('aria-expanded','false'); try { sessionStorage.setItem('fgFiniksDismissed', '1'); } catch {} (lastFocus || trigger).focus(); };
  trigger.addEventListener('click', open); panel.querySelector('[data-finiks-close]').addEventListener('click', close);
  panel.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const controls = [...panel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])')].filter(node => node.offsetParent !== null);
    if (!controls.length) return;
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  const findProducts = async query => {
    window.FiniksIntent?.record('catalogue_search');
    const status = add('Searching the live catalogue…');
    try {
      const url = new URL(config.predictiveSearchUrl, window.location.origin); url.searchParams.set('q', query); url.searchParams.set('resources[type]', 'product'); url.searchParams.set('resources[limit]', '4');
      const response = await fetch(url, { headers: { Accept: 'application/json' } }); if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json(); const products = data.resources?.results?.products || [];
      if (!products.length) { status.textContent = `No live matches for “${query}”. Try another search.`; return; }
      status.innerHTML = `<span>Live matches for “${escapeHTML(query)}”</span><div class="fg-intelligence__results">${products.map(product => `<a href="${escapeHTML(product.url)}"><b>${escapeHTML(product.title)}</b><span>${escapeHTML(product.price)}</span></a>`).join('')}</div>`;
    } catch { status.textContent = 'The catalogue is temporarily unavailable. Please use the store search.'; }
  };
  const askIntelligence = async question => {
    if (!config.intelligenceEndpoint) return false;
    const status = add('Checking verified sources…');
    try {
      const response = await fetch(config.intelligenceEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: question, pageType: config.pageType, productTitle: config.productTitle, locale: config.locale }) });
      if (!response.ok) throw new Error('Intelligence unavailable');
      const data = await response.json();
      if (!data.text) throw new Error('No answer');
      status.textContent = data.text;
      if (Array.isArray(data.citations) && data.citations.length) {
        const sources = document.createElement('div');
        sources.className = 'fg-intelligence__results';
        data.citations.forEach(citation => { const link = document.createElement('a'); link.href = citation.url; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.textContent = citation.title; sources.appendChild(link); });
        status.appendChild(sources);
      }
      addNextSteps(status, data.nextSteps);
      return true;
    } catch { status.remove(); return false; }
  };
  const recordLearningSignal = (event, path = '') => {
    if (!config.intelligenceEndpoint || document.hidden || !window.FiniksConsent?.getState?.().analyticsAllowed) return;
    const endpoint = new URL(config.intelligenceEndpoint, window.location.origin);
    endpoint.pathname = '/v1/events';
    endpoint.search = '';
    const body = JSON.stringify({ event, pageType: config.pageType, ...(safePath(path) ? { path: safePath(path) } : {}) });
    if (navigator.sendBeacon && body.length <= 256 && navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
    fetch(endpoint, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
  };
  const respond = async question => {
    const text = question.trim(); if (!text) return; window.FiniksIntent?.record('guide_question'); add(text, true); const q = text.toLowerCase();
    if (q.includes('cart') || q.includes('bag')) { window.FiniksIntent?.record('cart_opened'); recordLearningSignal('store_navigation'); add('Opening your bag.'); close(); document.querySelector('[data-cart-open]')?.click(); return; }
    if (q.includes('account') || q.includes('order') || q.includes('track')) { recordLearningSignal('store_navigation'); add('Your account is the protected place to see orders and tracking. Opening it now.'); window.location.assign(config.accountUrl); return; }
    if (q.includes('delivery') || q.includes('return')) { window.FiniksIntent?.record('delivery_request'); recordLearningSignal('delivery_policy'); add('Delivery options, arrival estimates and final return eligibility are confirmed for your location at checkout. For an existing order, use your secure account or contact support with its order number.'); return; }
    if (q.includes('support') || q.includes('contact')) { const note = 'Hello Finiks Gravity support,\n\nI need help with: '; if (!navigator.clipboard?.writeText) { add('Please email support@finiksgravity.com with your order number if applicable.'); return; } navigator.clipboard.writeText(note).then(() => add('A support message template has been copied. You can email it to support@finiksgravity.com.')).catch(() => add('Please email support@finiksgravity.com with your order number if applicable.')); return; }
    if (config.pageType === 'product' && config.productTitle && (q.includes('this') || q.includes('it'))) { add(`You are viewing ${config.productTitle}. The product page is the current source for variants, price, stock and specifications.`); return; }
    recordLearningSignal('general_question');
    if (await askIntelligence(text)) return;
    recordLearningSignal('catalogue_fallback');
    await findProducts(text.replace(/^(search|find|show me|i need)\s+/i, ''));
  };
  panel.querySelector('[data-finiks-form]').addEventListener('submit', event => { event.preventDefault(); const text = input.value; input.value = ''; respond(text); });
  panel.querySelectorAll('[data-finiks-action]').forEach(button => button.addEventListener('click', () => respond(button.dataset.finiksAction)));
  document.addEventListener('finiks:assistant-search', event => {
    const query = String(event.detail?.query || '').trim().slice(0, 80);
    if (!query) return;
    open();
    window.FiniksIntent?.record(event.detail?.source === 'voice' ? 'voice_search' : 'visual_search');
    respond(query);
  });
  window.addEventListener('offline', () => { if (panel.classList.contains('is-open')) add('You appear to be offline. Your message can be sent when your connection returns.'); });
})();


/* Collection discovery and recently viewed priority — v2026.9.10.5 */
(() => {
  'use strict';
    const viewedKey = 'finiksRecentlyViewedProduct';
      const cleanPath = (value) => (value || '').replace(/\?.*$/, '').replace(/\/$/, '');
        const productPath = cleanPath(window.location.pathname);

          if (productPath.indexOf('/products/') === 0) {
              try { localStorage.setItem(viewedKey, productPath); } catch (error) {}
                  return;
                    }
                      if (productPath.indexOf('/collections/') !== 0) return;

                        const shuffle = (items) => {
                            for (let index = items.length - 1; index > 0; index -= 1) {
                                  const swap = Math.floor(Math.random() * (index + 1));
                                        [items[index], items[swap]] = [items[swap], items[index]];
                                            }
                                                return items;
                                                  };

                                                    const enhanceCollection = () => {
                                                        let recent = '';
                                                            try { recent = cleanPath(localStorage.getItem(viewedKey)); } catch (error) {}
                                                                const grids = Array.from(document.querySelectorAll('.product-grid, .collection-grid, [data-product-grid], #product-grid'));

                                                                    grids.forEach((grid) => {
                                                                          if (grid.dataset.finiksDiscoveryReady === 'true') return;
                                                                                const cards = Array.from(grid.children).filter((card) => card.querySelector('a[href*="/products/"]'));
                                                                                      if (cards.length < 2) return;

                                                                                            const recentCard = recent ? cards.find((card) => {
                                                                                                    const productLink = card.querySelector('a[href*="/products/"]');
                                                                                                            return productLink && cleanPath(new URL(productLink.href, window.location.origin).pathname) === recent;
                                                                                                                  }) : null;
                                                                                                                        const freshCards = shuffle(cards.filter((card) => card !== recentCard));
                                                                                                                              (recentCard ? [recentCard].concat(freshCards) : freshCards).forEach((card) => grid.appendChild(card));

                                                                                                                                    if (recentCard && !recentCard.querySelector('.fg-recently-viewed')) {
                                                                                                                                            recentCard.dataset.finiksRecentCard = 'true';
                                                                                                                                                    const badge = document.createElement('span');
                                                                                                                                                            badge.className = 'fg-recently-viewed';
                                                                                                                                                                    badge.textContent = 'Recently viewed';
                                                                                                                                                                            recentCard.insertBefore(badge, recentCard.firstChild);
                                                                                                                                                                                  }
                                                                                                                                                                                        grid.dataset.finiksDiscoveryReady = 'true';
                                                                                                                                                                                            });
                                                                                                                                                                                              };

                                                                                                                                                                                                if (document.readyState === 'loading') {
                                                                                                                                                                                                    document.addEventListener('DOMContentLoaded', enhanceCollection, { once: true });
                                                                                                                                                                                                      } else {
                                                                                                                                                                                                          enhanceCollection();
                                                                                                                                                                                                            }
                                                                                                                                                                                                            })();
                                                                                                                                                                                                            
